import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FB_APIKEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FB_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage();
const areaCollectionRef = collection(db, 'plantAreas');
const plantCollectionsRef = collection(db, 'plants');

const logInWithEmailAndPassword = async (email, password) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    throw new Error(err.message);
  }
};

const logout = async () => {
  try {
    await signOut(auth);
  } catch (err) {
    throw new Error(err.message);
  }
};

const addArea = async (area) => {
  try {
    await setDoc(doc(areaCollectionRef), area);
  } catch (err) {
    throw new Error(err.message);
  }
};

const updateArea = async (area, id) => {
  try {
    const areaDocRef = doc(areaCollectionRef, id);
    await updateDoc(areaDocRef, area);
  } catch (err) {
    throw new Error(err.message);
  }
};

const getAllAreas = async () => {
  try {
    const q = query(areaCollectionRef, where('isDeleted', '==', false));
    const querySnapshot = await getDocs(q);
    return querySnapshot;
  } catch (err) {
    throw new Error(err.message);
  }
};

const getAllPlants = async (areaId) => {
  try {
    const q = query(plantCollectionsRef, where('areaId', '==', areaId));
    const querySnapshot = await getDocs(q);
    return querySnapshot;
  } catch (err) {
    throw new Error(err.message);
  }
};

const getArchivedPlants = async (areaId) => {
  try {
    const q = query(plantCollectionsRef, where('isArchived', '==', true));
    const querySnapshot = await getDocs(q);
    return querySnapshot;
  } catch (err) {
    throw new Error(err.message);
  }
};

const addPlant = async (plant) => {
  try {
    await setDoc(doc(plantCollectionsRef), plant);
  } catch (err) {
    throw new Error(err.message);
  }
};

const updatePlant = async (plant, plantId) => {
  try {
    const plantDocRef = doc(plantCollectionsRef, plantId);
    await updateDoc(plantDocRef, plant);
  } catch (err) {
    throw new Error(err.message);
  }
};

const deletePlant = async (plantId) => {
  try {
    const plantDocRef = doc(plantCollectionsRef, plantId);
    await deleteDoc(plantDocRef);
  } catch (err) {
    throw new Error(err.message);
  }
};

const uploadImage = async (file, name) => {
  try {
    const storageRef = ref(storage, `images/` + name);
    const imageSnapshot = await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(imageSnapshot.ref);
    return { name: name, url: downloadUrl };
  } catch (err) {
    throw new Error(err.message);
  }
};

const uploadImages = async (images) => {
  try {
    const promises = [];

    for (const i of images) {
      const storageRef = ref(storage, `images/` + i.name);

      promises.push(
        uploadBytes(storageRef, i.file)
          .then((uploadResult) => {
            return getDownloadURL(uploadResult.ref);
          })
          .then((url) => {
            return { name: i.name, url: url };
          })
      );
    }

    return await Promise.all(promises);
  } catch (err) {
    throw new Error(err.message);
  }
};

const deleteImage = async (name) => {
  try {
    const storageRef = ref(storage, `images/` + name);
    await deleteObject(storageRef);
  } catch (err) {
    throw new Error(err.message);
  }
};

const deleteImages = async (images) => {
  try {
    const promises = [];

    for (const i of images) {
      const storageRef = ref(storage, `images/` + i.name);

      promises.push(deleteObject(storageRef));
    }

    return await Promise.all(promises);
  } catch (err) {
    throw new Error(err);
  }
};

export {
  auth,
  db,
  logInWithEmailAndPassword,
  logout,
  addArea,
  updateArea,
  getAllAreas,
  getAllPlants,
  getArchivedPlants,
  updatePlant,
  addPlant,
  deletePlant,
  uploadImage,
  uploadImages,
  deleteImage,
  deleteImages,
};
